import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faClock,
  faCoins,
  faDatabase,
  faRefresh,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/components/dashboard/CacheMetrics.css';

function CacheMetrics({ user, isAdmin = false, tooltipComponent: TooltipIcon, tooltips }) {
  const [metricas, setMetricas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const cargarMetricas = async () => {
    try {
      // Intentar cargar métricas del caché inteligente
      try {
        const { default: firebaseOptimizado } = await import('../../utils/cache-inteligente-chile');
        const datos = firebaseOptimizado.getMetricas();
        setMetricas(datos);
      } catch (cacheError) {
        // Si falla el caché, mostrar métricas básicas
        setMetricas({
          cache: {
            activeEntries: 0,
            hitRate: '0%',
            costSavings: '$0.00 CLP',
            monthlySavings: '$0.00 CLP',
            memoryUsage: '0%'
          },
          operaciones: {
            lecturas: 0,
            escrituras: 0,
            cacheHits: 0,
            cacheMisses: 0,
            hitRate: '0%',
            costoEstimadoHoy: '$0.00 CLP'
          },
          recomendaciones: ['⚠️ Sistema de caché no disponible']
        });
      }
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error al cargar métricas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMetricas();
    
    // Actualizar métricas cada 30 segundos
    const interval = setInterval(cargarMetricas, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (hitRate) => {
    const rate = parseFloat(hitRate);
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'danger';
  };

  const getStatusIcon = (hitRate) => {
    const rate = parseFloat(hitRate);
    if (rate >= 80) return faCheckCircle;
    if (rate >= 60) return faExclamationTriangle;
    return faInfoCircle;
  };

  if (loading) {
    return (
      <div className="cache-metrics loading">
        <FontAwesomeIcon icon={faDatabase} spin />
        <span>Cargando métricas...</span>
      </div>
    );
  }

  if (!metricas) {
    return (
      <div className="cache-metrics error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <span>Error al cargar métricas</span>
      </div>
    );
  }

  const { cache, operaciones } = metricas;

  return (
    <div className="cache-metrics-container">
      <div className="metrics-header">
        <h3>
          <FontAwesomeIcon icon={faChartLine} />
          Métricas de Rendimiento
        </h3>
        <button 
          onClick={cargarMetricas}
          className="refresh-btn"
          title="Actualizar métricas"
        >
          <FontAwesomeIcon icon={faRefresh} />
        </button>
      </div>

      <div className="metrics-grid">
        {/* Caché Performance */}
        <div className="metric-card cache-performance">
          <div className="metric-header">
            <FontAwesomeIcon icon={faDatabase} />
            <h4>
              Rendimiento de Caché
              {TooltipIcon && <TooltipIcon tooltipKey="cacheHits" />}
            </h4>
          </div>
          <div className="metric-content">
            <div className="hit-rate">
              <span className={`rate-value ${getStatusColor(operaciones.hitRate)}`}>
                <FontAwesomeIcon icon={getStatusIcon(operaciones.hitRate)} />
                {operaciones.hitRate}
              </span>
              <span className="rate-label">
                Aciertos de Caché
                {TooltipIcon && <TooltipIcon tooltipKey="cacheHits" />}
              </span>
            </div>
            <div className="cache-stats">
              <div className="stat">
                <span className="value">{cache.activeEntries}</span>
                <span className="label">
                  Entradas Activas
                  {TooltipIcon && <TooltipIcon tooltipKey="dataSize" />}
                </span>
              </div>
              <div className="stat">
                <span className="value">{cache.memoryUsage}</span>
                <span className="label">
                  Uso Memoria
                  {TooltipIcon && <TooltipIcon tooltipKey="dataSize" />}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Costos */}
        <div className="metric-card cost-savings">
          <div className="metric-header">
            <FontAwesomeIcon icon={faCoins} />
            <h4>
              Ahorro de Costos
              {TooltipIcon && <TooltipIcon tooltipKey="savings" />}
            </h4>
          </div>
          <div className="metric-content">
            <div className="cost-today">
              <span className="cost-value">{cache.costSavings}</span>
              <span className="cost-label">
                Ahorrado Hoy
                {TooltipIcon && <TooltipIcon tooltipKey="savings" />}
              </span>
            </div>
            <div className="cost-projection">
              <span className="projection-value">{cache.monthlySavings}</span>
              <span className="projection-label">
                Proyección Mensual
                {TooltipIcon && <TooltipIcon tooltipKey="savings" />}
              </span>
            </div>
          </div>
        </div>

        {/* Operaciones */}
        {(user?.rol === 'admin' || isAdmin) && (
          <div className="metric-card operations">
            <div className="metric-header">
              <FontAwesomeIcon icon={faChartLine} />
              <h4>Operaciones Diarias</h4>
            </div>
            <div className="metric-content">
              <div className="operations-grid">
                <div className="operation">
                  <span className="op-value">{operaciones.lecturas}</span>
                  <span className="op-label">
                    Lecturas
                    {TooltipIcon && <TooltipIcon tooltipKey="responseTime" />}
                  </span>
                </div>
                <div className="operation">
                  <span className="op-value">{operaciones.escrituras}</span>
                  <span className="op-label">
                    Escrituras
                    {TooltipIcon && <TooltipIcon tooltipKey="responseTime" />}
                  </span>
                </div>
                <div className="operation">
                  <span className="op-value">{operaciones.cacheHits}</span>
                  <span className="op-label">
                    Aciertos Caché
                    {TooltipIcon && <TooltipIcon tooltipKey="cacheHits" />}
                  </span>
                </div>
                <div className="operation">
                  <span className="op-value">{operaciones.cacheMisses}</span>
                  <span className="op-label">
                    Fallos Caché
                    {TooltipIcon && <TooltipIcon tooltipKey="cacheMisses" />}
                  </span>
                </div>
              </div>
              <div className="cost-estimate">
                <span>Costo estimado hoy: {operaciones.costoEstimadoHoy}</span>
              </div>
            </div>
          </div>
        )}

        {/* Recomendaciones */}
        <div className="metric-card recommendations">
          <div className="metric-header">
            <FontAwesomeIcon icon={faInfoCircle} />
            <h4>Recomendaciones</h4>
          </div>
          <div className="metric-content">
            <ul className="recommendations-list">
              {metricas.recomendaciones.map((rec, index) => (
                <li key={index} className="recommendation">
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="metrics-footer">
        <div className="last-update">
          <FontAwesomeIcon icon={faClock} />
          <span>Última actualización: {lastUpdate.toLocaleTimeString('es-CL')}</span>
        </div>
        <div className="chile-flag">🇨🇱</div>
      </div>
    </div>
  );
}

export default CacheMetrics;